import { RejectSupplementModel } from "./types/order/RejectSupplementModel";
import { HttpClient } from "services/httpClient/HttpClient";
import { ApiError } from "./types/ApiError";
import { OrderListDto, SinchOrderList } from "./types/order/OrderListDto";
import { OrderDto } from "./types/order/OrderDto";
import queryString from "query-string";
import { ModifyModel } from "./types/port/ModifyModel";
import { NoteDto } from "./types/order/NoteDto";
import { AddNotesModel } from "./types/order/AddNotesModel";
import { TaskDto } from "./types/order/TaskDto";
import { AddTasksModel } from "./types/order/AddTasksModel";
import { ApiVersion } from "services/httpClient/ApiVersion";
import { NewSupplementModel, ServiceAddressDtoModel, SupplementCancelModel, SupplementDuedateModel, SupplementOthersModel } from "./types/order/NewSupplementModel";
import { AddDocumentSuccessModel } from "./types/order/AddDocumentSuccessModel";
import { UpdateOrderStatusModel } from "./types/order/UpdateOrderStatusModel"
import { NewModifyCsrModel } from "./types/order/NewModifyCsrModel";
import { CsrOnlyModel } from "./types/port/CsrOnlyModel";
import { ExportPortingReportDto } from "./types/reports/PortingReportDto";
import { OrderAutoActivationDto } from "./types/order/OrderAutoActivationDto";
import { SupplementType } from "./types/order/SupplementType";
import AddSupplementModal from "components/orders/orderDetails/AddSupplementModal";

export const OrderApi = {
  list: (search: string, spIds: string[], companyId: string | undefined, tenant: string | undefined = undefined): Promise<OrderListDto[]> => {

    let payload = {
        search: search && search.length > 0 ? search : undefined,
        spIds: spIds && spIds.length > 0 ? [spIds.join(',')] : undefined,
        companyId: companyId ? [companyId] : undefined
      };

    return HttpClient.post<OrderListDto[]>('order', payload, true, true, tenant, ApiVersion.V3);
  },

  drList: (isWholesale: boolean, companyId: string, tenant?: string): Promise<OrderListDto[]> => {
    return HttpClient.get<OrderListDto[]>(`disasterrecovery/listwithadminid?isWholesale=${isWholesale}&companyId=${companyId}`, true, true, tenant, ApiVersion.V3);
  },

  mixList: (search: string, companyId: string | undefined = undefined, clientName: string | undefined = undefined, tenant: string | undefined = undefined): Promise<OrderListDto[]> => {
    let url = queryString.stringifyUrl({
        url: "wholesale",
        query: {
          search: search && search.length > 0 ? search : undefined,
          companyId: companyId && companyId.length > 0 ? companyId : undefined,
          clientName: clientName && clientName.length > 0 ? clientName : undefined
        },
      }, {
      arrayFormat: 'comma'
    });

    return HttpClient.get<OrderListDto[]>(url, true, true, tenant, ApiVersion.V3);
  },

  get: (orderId: string, tenant?: string): Promise<OrderDto> => {
    return HttpClient.get<OrderDto>(`order/${orderId}`, true, true, tenant, ApiVersion.V2);
  },

  getDR: (orderId: string, tenant?: string): Promise<OrderDto> => {
    return HttpClient.get<OrderDto>(`disasterrecovery/adminid/${orderId}`, true, true, tenant, ApiVersion.V3);
  },

  modify: (orderId: string, tenant: string, modify: ModifyModel): Promise<ApiError> => {
    return HttpClient.put(`order/${orderId}`, modify, true, true, tenant);
  },
  drRestore: (orderId:any, tenant: string ): Promise<ApiError> => {
    return HttpClient.post<ApiError>('disasterrecovery/restore', orderId, true, true, tenant, ApiVersion.V3);
  },  

  patch: (orderId: string, projectId: string): Promise<ApiError> => {
    return HttpClient.patch(`order/${orderId}`, { projectId }, true, true);
  },

  activate: (orderId: string, tenant: string): Promise<ApiError> => {
    return HttpClient.post<ApiError>(`order/${orderId}/activate`, undefined, true, true, tenant);
  },

  cancel: (orderId: string, tenant: string): Promise<ApiError> => {
    return HttpClient.post<ApiError>(`order/${orderId}/cancel`, undefined, true, true, tenant);
  },

  removeFromConflict: (orderId: string, tenant: string): Promise<ApiError> => {
    return HttpClient.post<ApiError>(`order/${orderId}/conflict`, undefined, true, true, tenant);
  },

  archive: (orderId: string, tenant: string): Promise<ApiError> => {
    return HttpClient.post<ApiError>(`order/${orderId}/archive`, undefined, true, true, tenant);
  },

  undoCancel: (orderId: string, tenant: string): Promise<ApiError> => {
    return HttpClient.post<ApiError>(`order/${orderId}/undocancel`, undefined, true, true, tenant);
  },

  addSupplement: (
    orderId: string,
    tenant: string,
    supplementModel: NewSupplementModel
  ): Promise<ApiError> => {
    return HttpClient.post<ApiError>(
      `order/${orderId}/supplement/`,
      supplementModel,
      true,
      true,
      tenant,
      ApiVersion.V2
    );
  },

  addSupplementCancel: (
    orderId: string,
    tenant: string,
    supplementCancelModel: SupplementCancelModel
  ): Promise<ApiError> => {
    return HttpClient.post<ApiError>(
      `order/${orderId}/supplement/`,
      supplementCancelModel,
      true,
      true,
      tenant,
      ApiVersion.V2
    )
  },

  addSupplementDueDate: (
    orderId: string,
    tenant: string,
    supplementDueDateModel: SupplementDuedateModel
  ): Promise<ApiError> => {
    return HttpClient.post<ApiError>(
      `order/${orderId}/supplement/`,
      supplementDueDateModel,
      true,
      true,
      tenant,
      ApiVersion.V2
    )
  },

  addSupplementOthers: (
    orderId: string,
    tenant: string,
    supplementOthersModel: SupplementOthersModel
  ): Promise<ApiError> => {
    return HttpClient.post<ApiError>(
      `order/${orderId}/supplement/`,
      supplementOthersModel,
      true,
      true,
      tenant,
      ApiVersion.V2
    )
  },

  getServiceAddress:(
    orderIds: string[]
  ): Promise<ServiceAddressDtoModel[]> => {
    return HttpClient.get<ServiceAddressDtoModel[]>(
      `order/serviceaddress?orderIds=${orderIds}`,
      true,
      true,
      "",
      ApiVersion.V2
    )
  },

  approveSupplement: (orderId: string, tenant: string, focDate?: Date, supplementType?: string): Promise<ApiError> => {
    return HttpClient.post<ApiError>(
      `order/${orderId}/supplement/approve`,
      { focDate, supplementType },
      true,
      true,
      tenant,
      ApiVersion.V2
    );
  },

  rejectSupplement: (
    orderId: string,
    tenant: string,
    rejectSupplementModel: RejectSupplementModel
  ): Promise<ApiError> => {
    return HttpClient.post<ApiError>(
      `order/${orderId}/supplement/reject`,
      rejectSupplementModel,
      true,
      true,
      tenant,
      ApiVersion.V2
    );
  },

  updateOrderStatus: (
    orderId: string,
    tenant: string,
    updateOrderStatusModel: UpdateOrderStatusModel
  ): Promise<ApiError> => {
    return HttpClient.patch<ApiError>(
      `order/${orderId}`,
      updateOrderStatusModel,
      true,
      true,
      tenant,
      ApiVersion.V2
    );
  },

  modifyCsr: (
    orderId: string,
    tenant: string,
    modifyModel: NewModifyCsrModel
  ): Promise<ApiError> => {
    return HttpClient.post<ApiError>(
      `order/${orderId}/csr`,
      modifyModel,
      true,
      true,
      tenant,
      ApiVersion.V2
    );
  },

  csrOnlyOrder: (csrOnlyModel: CsrOnlyModel): Promise<ApiError> => {
    return HttpClient.post<ApiError>("order/csr", csrOnlyModel, true, true, "", ApiVersion.V2);
  },

  getNotes: (orderId: string): Promise<NoteDto[]> => {
    return HttpClient.get<NoteDto[]>(`order/${orderId}/notes`, true, true);
  },

  addNote: (orderId: string, noteModel: AddNotesModel): Promise<ApiError> => {
    return HttpClient.post<ApiError>(`order/${orderId}/notes`, noteModel, true, true);
  },

  getTasks: (orderId: string): Promise<TaskDto[]> => {
    return HttpClient.get<TaskDto[]>(`order/${orderId}/tasks`, true, true);
  },

  addTask: (orderId: string, taskModel: AddTasksModel): Promise<ApiError> => {
    return HttpClient.post<ApiError>(`order/${orderId}/tasks`, taskModel, true, true);
  },

  updateTask: (orderId: string, taskId: string, completed: boolean): Promise<ApiError> => {
    return HttpClient.patch(`order/${orderId}/tasks/${taskId}`, { completed }, true, true);
  },

  addDocument: (
    orderId: string,
    document: File,
    uploaderInfo?: { uploaderActualName: string; uploaderType: string }
  ) => {
    const data = new FormData();
    data.append("document", document);
    data.append("documentType", document.type);
    
    return HttpClient.postResource(
      `order/${orderId}/documents`,
      data,
      true,
      true
    );
  },

  getDocument: (orderId: string, documentId: string): Promise<ApiError | Blob> => {
    return HttpClient.getResource(`order/${orderId}/documents/${documentId}`, true, true);
  },

  deleteDocument: (orderId: string, documentId: string): Promise<ApiError> => {
    return HttpClient.delete<ApiError>(`order/${orderId}/documents/${documentId}`, {}, true, true);
  },

  portingReport: (from: Date, to: Date): Promise<ExportPortingReportDto[]> => {
    let url = queryString.stringifyUrl({
      url: "report/portingreport",
      query: {
        from: from.toISOString(),
        to: to.toISOString()
      }
    });

    return HttpClient.get<ExportPortingReportDto[]>(url, true, true);
  },
  setOnHold: (orderId: string, tenant: string): Promise<ApiError> => {
  return HttpClient.post<ApiError>(
    `order/${orderId}/hold`,
    {},
    true,
    true,
    tenant,
    ApiVersion.V3
  );
},

release: (orderId: string, tenant: string): Promise<ApiError> => {
  return HttpClient.post<ApiError>(
    `order/${orderId}/release`,
    {},
    true,
    true,
    tenant,
    ApiVersion.V3
  );
},
};
